// src/components/reports/GuideTab.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  Users,
  Target,
  Download,
  Printer,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  CalendarCheck,
  ListTodo,
  TrendingUp,
  HelpCircle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Zap,
  Rocket,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { possessive } from '../../utils/reportHelpers';

const GUIDE_STEPS_KEY = (reportId) => `guide_progress_${reportId}`;
const SEVEN_DAY_KEY  = (reportId) => `seven_day_done_${reportId}`;

// ── Build 7 micro-tasks from real report data ────────────────────────────────
const buildSevenDayPlan = (phases, adoptionChecklist, progress = {}) => {
  const p1Tools = phases[0]?.tools || [];
  const week1 = (adoptionChecklist || []).find(
    (item) => !item.recommendedWeek || item.recommendedWeek <= 1
  );
  const t0 = p1Tools[0]?.name;
  const t0Id = p1Tools[0]?.id ?? p1Tools[0]?.name;
  const t1 = p1Tools[1]?.name;

  // If the first tool is already done/in-progress, use the next not-started tool for Day 2
  const t0Done = t0Id && (progress[t0Id] === 'done' || progress[t0Id] === 'in_progress');
  const nextNotStarted = t0Done
    ? p1Tools.find((t) => {
        const id = t.id ?? t.name;
        return !progress[id] || progress[id] === 'not_started';
      })
    : null;
  const day2Tool = nextNotStarted?.name ?? t0;

  return [
    {
      day: 1, icon: '🔍',
      label: t0 ? `Explore ${t0}` : 'Review your top 3 tools',
      action: t0
        ? `Spend 15 mins on ${possessive(t0)} website. Watch the intro video — don't sign up yet, just understand what it does for businesses like yours.`
        : 'Open the Tools tab. Read the "why selected" for your top 3 picks and note which one solves your #1 problem.',
      time: '15 min',
    },
    {
      day: 2, icon: '🔑',
      label: day2Tool ? `Sign up for ${day2Tool}` : 'Create your first free account',
      action: day2Tool
        ? `Create your free trial account for ${day2Tool}. Don't configure anything yet — just get access and note your login.`
        : 'Sign up for your top recommended tool. Getting access is the only goal today.',
      time: '20 min',
    },
    {
      day: 3, icon: '⚡',
      label: t0 ? `Run your first task in ${t0}` : 'Complete your first AI task',
      action: t0
        ? `Do one real task with ${t0} using your own data. A messy first result is fine — the goal is to feel how it works.`
        : 'Use your top tool on a real workflow. Done imperfectly beats not started.',
      time: '30 min',
    },
    {
      day: 4, icon: t1 ? '🛠️' : '👥',
      label: t1 ? `Explore ${t1}` : 'Share your plan with one person',
      action: t1
        ? `Spend 15 mins learning about ${t1}. Sign up for the free tier if it looks like a fit.`
        : 'Send this report link to one teammate. Ask them: "Which tool here would help your day most?"',
      time: '20 min',
    },
    {
      day: 5, icon: '💬',
      label: 'Run your first AI prompt',
      action: 'Go to the Prompts tab. Copy prompt #1 into ChatGPT, Claude, or Grok with your real business context. Read the output — don\'t edit it yet, just experience what AI can do.',
      time: '20 min',
    },
    {
      day: 6, icon: '📊',
      label: week1
        ? (week1.text.length > 55 ? `${week1.text.slice(0, 55)}…` : week1.text)
        : 'Pick your one metric to track',
      action: week1
        ? week1.text
        : 'Go to the Financials tab. Choose one KPI to measure for the next 30 days. Write it somewhere you\'ll see every morning.',
      time: '25 min',
    },
    {
      day: 7, icon: '📅',
      label: 'Lock weeks 2–4 into your calendar',
      action: 'Block 30 minutes every week for the next 3 weeks to continue implementation. Set a recurring calendar event right now — consistency beats intensity.',
      time: '10 min',
    },
  ];
};

// Step component with collapsible details
const PhaseStep = ({
  step,
  phaseId,
  stepIndex,
  completed,
  onToggle,
  expanded,
  onExpand,
}) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      className="border border-white/10 rounded-xl mb-3 overflow-hidden bg-slate-900/70 backdrop-blur-xl"
    >
      <div
        className="flex items-center p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => onExpand(stepIndex)}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle(stepIndex);
          }}
          className="mr-3 focus:outline-none"
          aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
        >
          {completed ? (
            <CheckCircle2 className="w-6 h-6 text-[#3b82f6]" />
          ) : (
            <Circle className="w-6 h-6 text-slate-500 hover:text-[#3b82f6] transition-colors" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-medium truncate ${
              completed
                ? 'text-slate-500 line-through'
                : 'text-white'
            }`}
          >
            {step.action}
          </p>
          {step.tool && (
            <Badge className="mt-1 bg-blue-500/20 text-blue-300 text-xs">
              Tool: {step.tool}
            </Badge>
          )}
        </div>
        <div className="text-slate-400 ml-2">
          {expanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-4 pt-2 border-t border-white/8"
          >
            <p className="text-sm text-slate-400 mb-3">
              {step.description || 'No additional details.'}
            </p>
            {/* Owner & time expectations */}
            {(step.ownerRole || step.estimatedTime) && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                {step.ownerRole && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" /> Owner:{' '}
                    {step.ownerRole}
                  </span>
                )}
                {step.estimatedTime && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Est.{' '}
                    {step.estimatedTime}
                  </span>
                )}
              </div>
            )}
            {step.resources && step.resources.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500 mb-1">
                  Resources
                </p>
                <ul className="space-y-1">
                  {step.resources.map((res, idx) => (
                    <li key={idx}>
                      <a
                        href={res.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />{' '}
                        {res.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Gantt chart ──────────────────────────────────────────────────────────────
const GANTT_COLORS = [
  { bar: 'bg-blue-500/60 border border-[#3b82f6]/50', text: 'text-blue-300', badge: 'bg-blue-500/15 text-blue-300 border border-blue-500/20' },
  { bar: 'bg-blue-500/60 border border-blue-400/50',       text: 'text-blue-300',    badge: 'bg-blue-500/15 text-blue-300 border border-blue-500/20' },
  { bar: 'bg-purple-500/60 border border-purple-400/50',   text: 'text-purple-300',  badge: 'bg-purple-500/15 text-purple-300 border border-purple-500/20' },
  { bar: 'bg-amber-500/60 border border-amber-400/50',     text: 'text-amber-300',   badge: 'bg-amber-500/15 text-amber-300 border border-amber-500/20' },
];

const GANTT_START_KEY = (reportId) => `gantt_start_${reportId}`;

const RoadmapGantt = ({ phases, startDate: propStartDate, reportId }) => {
  const [pickedDate, setPickedDate] = useState(() => {
    try {
      const key = GANTT_START_KEY(reportId || 'default');
      return localStorage.getItem(key) || propStartDate || '';
    } catch { return propStartDate || ''; }
  });

  const handleDateChange = (e) => {
    const val = e.target.value;
    setPickedDate(val);
    try {
      const key = GANTT_START_KEY(reportId || 'default');
      if (val) localStorage.setItem(key, val);
      else localStorage.removeItem(key);
    } catch {}
  };

  if (!phases || phases.length === 0) return null;

  const startDate = pickedDate || propStartDate;
  const totalWeeks = phases.reduce((sum, p) => sum + (p.weeks || 4), 0);
  const weekLabels = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  let cursor = 0;
  const slots = phases.map((p, idx) => {
    const start = cursor;
    const dur = p.weeks || 4;
    cursor += dur;
    return { ...p, startWeek: start, duration: dur, color: GANTT_COLORS[idx % GANTT_COLORS.length] };
  });

  // Current week marker
  let currentWeek = null;
  if (startDate) {
    const diff = Math.floor((Date.now() - new Date(startDate)) / (1000 * 60 * 60 * 24 * 7));
    if (diff >= 0 && diff < totalWeeks) currentWeek = diff;
  }

  // Build calendar date label for a given week offset
  const weekDateLabel = (weekIdx) => {
    if (!startDate) return `Wk ${weekIdx + 1}`;
    const d = new Date(startDate);
    d.setDate(d.getDate() + weekIdx * 7);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-white text-base">
            <Calendar className="w-4 h-4 text-blue-400" />
            {totalWeeks}-Week Roadmap at a Glance
          </CardTitle>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400 whitespace-nowrap">Start date:</label>
            <input
              type="date"
              value={pickedDate || ''}
              onChange={handleDateChange}
              className="bg-slate-950/50 border border-white/15 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500/50"
            />
            {pickedDate && (
              <button
                type="button"
                onClick={() => handleDateChange({ target: { value: '' } })}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mobile: list view */}
        <div className="sm:hidden space-y-3">
          {slots.map((phase, idx) => {
            const toolNames = (phase.tools || []).slice(0, 3).map(t => t.name).filter(Boolean).join(', ');
            const startLabel = weekDateLabel(phase.startWeek);
            const endLabel = weekDateLabel(phase.startWeek + phase.duration);
            return (
              <div key={idx} className={`rounded-xl border p-3 ${phase.color.badge}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold ${phase.color.text}`}>{phase.name}</span>
                  <span className="text-[10px] text-slate-400">{startLabel} → {endLabel}</span>
                </div>
                {toolNames && <p className="text-[11px] text-slate-300">{toolNames}</p>}
              </div>
            );
          })}
        </div>

        {/* Desktop: Gantt chart */}
        <div className="hidden sm:block overflow-x-auto">
          <div style={{ minWidth: `${Math.max(totalWeeks * 36, 320)}px` }}>

            {/* Week header */}
            <div className="flex mb-1 pl-28">
              {weekLabels.map(w => (
                <div
                  key={w}
                  className="flex-1 text-center text-[9px] font-medium text-slate-500"
                >
                  {w}
                </div>
              ))}
            </div>

            {/* Phase rows */}
            {slots.map((phase, idx) => {
              const leftPct  = (phase.startWeek / totalWeeks) * 100;
              const widthPct = (phase.duration  / totalWeeks) * 100;
              const toolNames = (phase.tools || []).slice(0, 2).map(t => t.name).filter(Boolean).join(' · ');
              return (
                <div key={idx} className="flex items-center mb-2 gap-2">
                  {/* Phase label */}
                  <div className="w-28 flex-shrink-0 pr-2 text-right">
                    <span className={`text-[10px] font-semibold leading-tight ${phase.color.text}`}>
                      {phase.name}
                    </span>
                  </div>

                  {/* Bar track */}
                  <div className="flex-1 relative h-7 bg-white/6 rounded-lg overflow-hidden">
                    {/* Subtle week grid lines */}
                    {weekLabels.map(w => (
                      <div
                        key={w}
                        className="absolute inset-y-0 w-px bg-white/8"
                        style={{ left: `${(w / totalWeeks) * 100}%` }}
                      />
                    ))}
                    {/* Current week marker */}
                    {currentWeek !== null && (
                      <div
                        className="absolute inset-y-0 w-0.5 bg-white/30 z-10"
                        style={{ left: `${((currentWeek + 0.5) / totalWeeks) * 100}%` }}
                      />
                    )}
                    {/* Phase bar */}
                    <div
                      className={`absolute inset-y-1 rounded-md flex items-center px-2 ${phase.color.bar}`}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    >
                      <span className="text-[9px] text-white/90 font-medium truncate">
                        {toolNames || phase.focus}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Week label row (bottom) */}
            <div className="flex pl-28 mt-1 border-t border-white/8 pt-1">
              {weekLabels.map(w => (
                <div key={w} className="flex-1 text-center text-[9px] text-slate-600">
                  {startDate
                    ? (w % 3 === 0 ? weekDateLabel(w - 1) : '')
                    : (w % 2 === 0 ? `Wk ${w}` : '')}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-2 mt-3">
          {slots.map((phase, idx) => (
            <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-full ${phase.color.badge}`}>
              {startDate
                ? `${phase.name}: ${weekDateLabel(phase.startWeek)} – ${weekDateLabel(phase.startWeek + phase.duration)}`
                : `${phase.name}: Wk ${phase.startWeek + 1}–${phase.startWeek + phase.duration}`}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const GuideTab = ({
  phases = [],
  actionSteps = [],
  implementationTimeline = {},
  adoptionChecklist = [],
  playbookProgress,   // controlled: { [stepId]: "done" | "not_started" }
  onPlaybookUpdate,   // controlled: (stepId, status) => void
  topProblem = null,
  topTool = null,
  topPlaybook = null,
  topPrompt = null,
  onAskAI = null,     // (prompt: string) => void — opens Ask AI tab prefilled
  allTools = [],      // full tool list for "Start with 2" strip
  progress = {},      // tool progress map { [toolId]: 'not_started' | 'in_progress' | 'done' }
}) => {
  const { reportId } = useParams();
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const [expandedDay, setExpandedDay] = useState(null);

  // Seven-day plan completion (localStorage)
  const [sevenDayDone, setSevenDayDone] = useState(() => {
    try {
      const saved = localStorage.getItem(SEVEN_DAY_KEY(reportId));
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const sevenDayPlan = useMemo(
    () => buildSevenDayPlan(phases, adoptionChecklist, progress),
    [phases, adoptionChecklist, progress]
  );

  const toggleSevenDay = useCallback((day) => {
    setSevenDayDone((prev) => {
      const next = { ...prev, [day]: !prev[day] };
      try { localStorage.setItem(SEVEN_DAY_KEY(reportId), JSON.stringify(next)); } catch {}
      return next;
    });
  }, [reportId]);

  // ── Weekly checklist completion tracking ─────────────────────────────────
  const [expandedWeeklyPhase, setExpandedWeeklyPhase] = useState(0); // Phase 1 open by default
  const [weeklyChecked, setWeeklyChecked] = useState(() => {
    try {
      const saved = localStorage.getItem(`weekly_checklist_${reportId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const toggleWeeklyItem = useCallback((itemId) => {
    setWeeklyChecked(prev => {
      const next = { ...prev, [itemId]: !prev[itemId] };
      try { localStorage.setItem(`weekly_checklist_${reportId}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [reportId]);
  // Internal state used when parent doesn't pass controlled props
  const [completedSteps, setCompletedSteps] = useState(() => {
    if (playbookProgress) return {};  // controlled mode — skip localStorage init
    try {
      const saved = localStorage.getItem(GUIDE_STEPS_KEY(reportId));
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Save to localStorage only when in uncontrolled mode
  useEffect(() => {
    if (playbookProgress || !reportId) return;
    try {
      localStorage.setItem(
        GUIDE_STEPS_KEY(reportId),
        JSON.stringify(completedSteps)
      );
    } catch (e) {
      console.warn('Failed to save guide progress', e);
    }
  }, [completedSteps, reportId, playbookProgress]);

  // Flatten all steps across phases for easy completion tracking
  const allSteps = useMemo(() => {
    const steps = [];
    phases.forEach((phase, pIdx) => {
      phase.tools?.forEach((tool, tIdx) => {
        steps.push({
          id: `phase-${pIdx}-tool-${tIdx}`,
          phaseId: pIdx,
          action: `Set up ${tool.name}`,
          description:
            tool.why ||
            `Follow the tool-specific setup steps in the Tools tab and the "${phase.name}" phase.`,
          tool: tool.name,
          ownerRole:
            tool.ownerRole ||
            phase.ownerHint ||
            'Implementation lead',
          estimatedTime: tool.setupHours
            ? `${tool.setupHours} hours`
            : null,
        });
      });
    });
    // Add actionSteps as independent steps
    actionSteps.forEach((step, idx) => {
      steps.push({
        id: `action-${idx}`,
        phaseId: null,
        action: step.action,
        description: step.description,
        tool: step.tool,
        ownerRole: step.ownerRole,
        estimatedTime: step.estimatedTime,
      });
    });
    return steps;
  }, [phases, actionSteps]);

  const toggleStep = (stepId) => {
    if (onPlaybookUpdate) {
      const isDone = playbookProgress?.[stepId] === 'done';
      onPlaybookUpdate(stepId, isDone ? 'not_started' : 'done');
    } else {
      setCompletedSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
    }
  };

  // Resolve whether a step is completed — controlled or uncontrolled
  const isStepDone = (stepId) =>
    playbookProgress ? playbookProgress[stepId] === 'done' : !!completedSteps[stepId];

  const toggleStepExpanded = (stepId) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) newSet.delete(stepId);
      else newSet.add(stepId);
      return newSet;
    });
  };

  const completedCount = allSteps.filter((s) => isStepDone(s.id)).length;
  const progressPercent = allSteps.length
    ? (completedCount / allSteps.length) * 100
    : 0;

  const weekByWeek = implementationTimeline.weekByWeek || [];
  const quarterlyReviews =
    implementationTimeline.quarterlyReviews || [];
  const metrics = implementationTimeline.metrics || [];

  // Determine current week (for highlighting) – simple placeholder
  const startDate = implementationTimeline.startDate
    ? new Date(implementationTimeline.startDate)
    : null;
  let currentWeek = null;
  if (startDate) {
    const today = new Date();
    const diffDays = Math.floor(
      (today - startDate) / (1000 * 60 * 60 * 24)
    );
    currentWeek = Math.floor(diffDays / 7) + 1;
  }

  // CSV export
  const exportStepsAsCSV = () => {
    const rows = [
      ['Phase', 'Action', 'Tool', 'Owner', 'Estimated Time', 'Completed'],
      ...allSteps.map((s) => [
        (s.phaseId !== null && phases[s.phaseId]?.name) ||
          'General',
        s.action,
        s.tool || '',
        s.ownerRole || '',
        s.estimatedTime || '',
        isStepDone(s.id) ? 'Yes' : 'No',
      ]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((v) => `"${(v || '').replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AI_Implementation_Guide_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Guide exported as CSV');
  };

  const sevenDayCompleted = sevenDayPlan.filter((t) => sevenDayDone[t.day]).length;

  const tool1 = allTools[0] || null;
  const tool2 = allTools[1] || null;

  return (
    <div className="space-y-8">

      {/* ── START HERE: Next 15 Minutes ── */}
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">⚡</span>
          <span className="text-sm font-bold text-amber-400 uppercase tracking-wider">Do This in the Next 15 Minutes</span>
        </div>
        <div className="space-y-2.5">
          {[
            { n: '1', text: `Sign up for ${topTool || 'your #1 tool'}`, sub: `Go to Day 1 below and click the link — most offer a free trial. ${topProblem ? `This directly solves: "${typeof topProblem === 'string' ? topProblem : topProblem.problem || topProblem}"` : ''}` },
            { n: '2', text: `Run your first ${topPlaybook || 'playbook'}`, sub: `Open the Playbooks tab and follow the first one step-by-step — it's built for your exact situation` },
            { n: '3', text: 'Copy your first prompt', sub: `Go to the Prompts tab → copy the top prompt → paste it into your AI assistant and run it right now` },
          ].map(({ n, text, sub }) => (
            <div key={n} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 text-xs font-bold flex items-center justify-center mt-0.5">{n}</span>
              <div>
                <p className="text-sm font-semibold text-white leading-snug">{text}</p>
                <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
        {onAskAI && topTool && (
          <button
            onClick={() => onAskAI(`Give me a step-by-step setup checklist for ${topTool} customized for my business. Include the signup link, first configuration steps, and what I should do in my first hour.`)}
            className="mt-4 text-xs text-amber-400 hover:text-amber-300 border border-amber-500/30 hover:border-amber-400/50 rounded-full px-3 py-1.5 transition-colors inline-flex items-center gap-1.5"
          >
            💬 Ask AI: "Walk me through setting up {topTool} step by step"
          </button>
        )}
      </div>

      {/* ── Start With These 2 Tools ── */}
      {tool1 && tool2 && (
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-5">
          <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-1">If You Only Do Two Things This Month — Do These</p>
          <p className="text-xs text-slate-500 mb-4">Your top tools, ranked by impact on your biggest problem first</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[tool1, tool2].map((tool, i) => {
              const name = tool.name || tool.tool_name || 'Tool';
              // Pull the per-tool match reason produced by the recommendation
              // engine — references the user's actual matched problem, the
              // tool's mechanism, and how many of their selected problems it
              // covers. The previous fallback ("Directly targets your highest-
              // priority problem.") rendered identically for Step 1 AND Step 2,
              // making the section feel like template-noise.
              const why = tool.matchReason
                || tool.why_selected
                || tool.why
                || tool.why_recommended
                || tool.use_case
                || (tool.notes ? `${tool.notes.replace(/\.$/, '')} — top-${i + 1} pick by problem fit.` : `Top ${i === 0 ? 'pick' : 'follow-up'} by match score.`);
              return (
                <div key={name} className="bg-slate-950/50 border border-white/8 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${i === 0 ? 'bg-blue-500/20 text-[#3b82f6] border border-blue-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                      Step {i + 1} — Must Start
                    </span>
                  </div>
                  <p className="text-sm font-bold text-white mb-1">{name}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{why}</p>
                  {onAskAI && (
                    <button
                      onClick={() => onAskAI(`How do I set up ${name} for my business? Give me a simple checklist.`)}
                      className="mt-3 text-[10px] text-slate-400 hover:text-[#3b82f6] transition-colors inline-flex items-center gap-1"
                    >
                      💬 Ask AI to build my setup checklist
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-600 mt-3">Additional tools are in the 🛠️ Your Tools tab — start these two first, then layer in the rest.</p>
        </div>
      )}

      {/* ── Implementation Chain ── */}
      {topProblem && topTool && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <p className="text-xs font-semibold text-[#3b82f6] uppercase tracking-wider mb-3">Your Implementation Chain — Problem to Solution</p>
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold text-xs mt-0.5 w-16 flex-shrink-0">Problem</span>
              <span className="text-slate-200 text-sm leading-snug">{topProblem}</span>
            </div>
            <div className="pl-16 text-slate-600 text-xs">↓</div>
            <div className="flex items-start gap-3">
              <span className="text-amber-400 font-bold text-xs mt-0.5 w-16 flex-shrink-0">Tool</span>
              <span className="text-slate-200 text-sm font-medium">{topTool}</span>
            </div>
            {topPlaybook && (
              <>
                <div className="pl-16 text-slate-600 text-xs">↓</div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold text-xs mt-0.5 w-16 flex-shrink-0">Playbook</span>
                  <span className="text-slate-200 text-sm">{topPlaybook}</span>
                </div>
              </>
            )}
            {topPrompt && (
              <>
                <div className="pl-16 text-slate-600 text-xs">↓</div>
                <div className="flex items-start gap-3">
                  <span className="text-purple-400 font-bold text-xs mt-0.5 w-16 flex-shrink-0">Prompt</span>
                  <span className="text-slate-200 text-sm">{topPrompt}</span>
                </div>
              </>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-3">Full playbooks in the 📝 Prompts tab · Full tool details in 🛠️ Your Tools</p>
        </div>
      )}

      {/* ── First 7 Days ── */}
      <div className="rounded-2xl border border-blue-500/30 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(15,23,42,0.99) 100%)' }}>
        <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="w-4 h-4 text-[#3b82f6]" />
            <span className="font-semibold text-white text-sm">Your First 7 Days — Start Here</span>
            <Badge className="bg-blue-500/15 text-[#3b82f6] border border-blue-500/20 text-xs ml-1">
              {sevenDayCompleted}/7 done
            </Badge>
          </div>
          <span className="text-xs text-slate-500 hidden sm:block">15–30 min per day · no technical skills needed</span>
        </div>

        <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {sevenDayPlan.map((task) => {
            const done = !!sevenDayDone[task.day];
            const isOpen = expandedDay === task.day;
            return (
              <div key={task.day}
                className={`rounded-xl border transition-all ${
                  done
                    ? 'bg-blue-500/8 border-blue-500/25 opacity-70'
                    : 'bg-slate-950/50 border-white/8 hover:border-white/15'
                }`}
              >
                <button
                  className="w-full text-left p-4"
                  onClick={() => setExpandedDay(isOpen ? null : task.day)}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSevenDay(task.day); }}
                      className="flex-shrink-0 mt-0.5"
                      aria-label={done ? 'Mark incomplete' : 'Mark complete'}
                    >
                      {done
                        ? <CheckCircle2 className="w-5 h-5 text-[#3b82f6]" />
                        : <Circle className="w-5 h-5 text-slate-500 hover:text-[#3b82f6] transition-colors" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Day {task.day}</span>
                        <span className="text-[10px] text-slate-600">·</span>
                        <span className="text-[10px] text-slate-500">{task.time}</span>
                      </div>
                      <p className={`text-sm font-semibold leading-snug ${done ? 'line-through text-slate-500' : 'text-white'}`}>
                        {task.icon} {task.label}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-slate-600 mt-0.5">
                      {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </div>
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="px-4 pb-4 pt-1 border-t border-white/8">
                        <p className="text-xs text-slate-300 leading-relaxed">{task.action}</p>
                        {onAskAI && (
                          <button
                            onClick={() => onAskAI(`I'm on Day ${task.day} of my AI blueprint. The task is: ${task.label}. I'm stuck — what exactly should I do right now, step by step?`)}
                            className="mt-2.5 text-[10px] text-[#3b82f6] hover:text-blue-300 border border-blue-500/25 hover:border-[#3b82f6]/50 rounded-full px-2.5 py-1 transition-colors inline-flex items-center gap-1"
                          >
                            💬 Stuck on Day {task.day}? Ask your coach →
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {sevenDayCompleted === 7 && (
          <div className="px-6 pb-5 text-center">
            <p className="text-[#3b82f6] font-semibold text-sm">
              🎉 First week complete! Move to Phase 1 in the roadmap below.
            </p>
          </div>
        )}
      </div>

      {/* ── Gantt Roadmap ── */}
      <RoadmapGantt phases={phases} startDate={implementationTimeline.startDate} reportId={reportId} />

      {/* How-to-use box */}
      <div className="bg-slate-950/50 border border-white/10 rounded-xl p-4">
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-[#3b82f6]">
            Playbook:
          </span>{' '}
          Assign each step to an owner, schedule it on your
          calendar, and review progress weekly. Plan Mondays,
          implement Tuesday–Thursday, review Fridays.
        </p>
      </div>

      {/* Header with progress */}
      <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Rocket className="h-6 w-6 text-[#3b82f6]" /> Your
            Implementation Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 mb-6">
            Track your progress week by week. Mark steps as done,
            and we'll save your progress automatically.
          </p>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-slate-400">
              <span>Overall progress</span>
              <span className="text-[#3b82f6] font-medium">
                {completedCount}/{allSteps.length} steps
              </span>
            </div>
            <Progress
              value={progressPercent}
              className="h-2 bg-white/10"
              indicatorClassName="bg-gradient-to-r from-blue-500 to-teal-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Week-by-Week Roadmap (built from adoptionChecklist + phases) ── */}
      {Array.isArray(adoptionChecklist) && adoptionChecklist.length > 0 && phases.length > 0 && (() => {
        // Build phase week boundaries
        let cursor = 0;
        const phaseBounds = phases.map(p => {
          const start = cursor + 1;
          cursor += p.weeks || 4;
          return { start, end: cursor };
        });
        const totalWeeks = cursor;

        // Group checklist items by week
        const byWeek = {};
        adoptionChecklist.forEach(item => {
          const w = item.recommendedWeek ?? 1;
          if (!byWeek[w]) byWeek[w] = [];
          byWeek[w].push(item);
        });

        const phaseColors = [
          { text: 'text-[#3b82f6]', bg: 'bg-blue-500/10', border: 'border-blue-500/30', weekBadge: 'bg-blue-500/20 text-blue-300 border-blue-500/25' },
          { text: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    weekBadge: 'bg-blue-500/20 text-blue-300 border-blue-500/25' },
          { text: 'text-purple-400',  bg: 'bg-purple-500/10',  border: 'border-purple-500/30',  weekBadge: 'bg-purple-500/20 text-purple-300 border-purple-500/25' },
          { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   weekBadge: 'bg-amber-500/20 text-amber-300 border-amber-500/25' },
        ];

        const totalItems = adoptionChecklist.length;
        const doneItems = Object.values(weeklyChecked).filter(Boolean).length;

        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#3b82f6]" />
                {totalWeeks}-Week Implementation Plan
              </h3>
              <span className="text-xs text-slate-400 bg-slate-950/50 border border-white/8 px-3 py-1 rounded-full">
                {doneItems}/{totalItems} tasks done
              </span>
            </div>

            <div className="space-y-3">
              {phases.map((phase, pIdx) => {
                const c = phaseColors[pIdx % phaseColors.length];
                const { start, end } = phaseBounds[pIdx];

                // Collect weeks in this phase that have items
                const weeksWithItems = [];
                for (let w = start; w <= end; w++) {
                  if (byWeek[w]?.length) {
                    weeksWithItems.push({ week: w, items: byWeek[w] });
                  }
                }

                const phaseItemCount = weeksWithItems.flatMap(pw => pw.items).length;
                const phaseDoneCount = weeksWithItems.flatMap(pw => pw.items).filter(item => weeklyChecked[item.id]).length;
                const isExpanded = expandedWeeklyPhase === pIdx;
                const progressPct = phaseItemCount > 0 ? Math.round((phaseDoneCount / phaseItemCount) * 100) : 0;

                return (
                  <div key={pIdx} className={`rounded-2xl border ${c.border} overflow-hidden`}>
                    {/* Phase header — clickable to expand */}
                    <button
                      className={`w-full px-5 py-4 ${c.bg} flex items-start justify-between gap-3 text-left hover:brightness-110 transition-all`}
                      onClick={() => setExpandedWeeklyPhase(isExpanded ? null : pIdx)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className={`text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${c.text}`}>
                            Phase {pIdx + 1} · Weeks {start}–{end}
                          </span>
                          {phaseItemCount > 0 && progressPct === 100 && (
                            <span className="text-[10px] bg-blue-500/20 text-[#3b82f6] px-1.5 py-0.5 rounded-full">Complete ✓</span>
                          )}
                        </div>
                        <p className="text-white font-semibold text-sm">{phase.name}</p>
                        {phase.focus && (
                          <p className="text-xs mt-0.5 text-slate-400">{phase.focus}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {(phase.tools || []).length > 0 && (
                            <span className="text-[10px] text-slate-500 bg-white/5 border border-white/8 px-1.5 py-0.5 rounded">
                              {(phase.tools || []).length} tools
                            </span>
                          )}
                          {phase.estimated_monthly_cost && (
                            <span className="text-[10px] text-slate-500 bg-white/5 border border-white/8 px-1.5 py-0.5 rounded">
                              ~${phase.estimated_monthly_cost}/mo
                            </span>
                          )}
                          {phase.expected_impact && (
                            <span className={`text-[10px] ${c.text} opacity-70`}>{phase.expected_impact}</span>
                          )}
                        </div>
                        {/* Progress bar */}
                        {phaseItemCount > 0 && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-400 flex-shrink-0">{phaseDoneCount}/{phaseItemCount}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 mt-1">
                        {isExpanded
                          ? <ChevronUp className="w-4 h-4 text-slate-400" />
                          : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {/* Week-by-week breakdown */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          style={{ overflow: 'hidden' }}
                        >
                          {weeksWithItems.length === 0 ? (
                            <div className="px-5 py-4 text-sm text-slate-500 italic">
                              No scheduled tasks for this phase — check your implementation guide.
                            </div>
                          ) : (
                            <div className="divide-y divide-white/8">
                              {weeksWithItems.map(({ week, items }) => (
                                <div key={week} className="px-5 py-3">
                                  <div className="flex items-center gap-2 mb-2.5">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.weekBadge}`}>
                                      Week {week}
                                    </span>
                                  </div>
                                  <div className="space-y-2">
                                    {items.map(item => {
                                      const done = !!weeklyChecked[item.id];
                                      return (
                                        <div key={item.id} className="flex items-start gap-2.5">
                                          <button
                                            onClick={() => toggleWeeklyItem(item.id)}
                                            className="flex-shrink-0 mt-0.5"
                                            aria-label={done ? 'Mark incomplete' : 'Mark complete'}
                                          >
                                            {done
                                              ? <CheckCircle2 className="w-4 h-4 text-[#3b82f6]" />
                                              : <Circle className="w-4 h-4 text-slate-500 hover:text-[#3b82f6] transition-colors" />}
                                          </button>
                                          <div className="min-w-0">
                                            <p className={`text-sm leading-snug ${done ? 'line-through text-slate-500' : 'text-slate-200'}`}>
                                              {item.text}
                                            </p>
                                            {item.category && (
                                              <span className="text-[10px] text-slate-500 mt-0.5 block uppercase tracking-wide">{item.category}</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Week‑by‑Week Plan */}
      {weekByWeek.length > 0 && (
        <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#3b82f6]">
              <Calendar className="h-5 w-5" /> Week‑by‑Week Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {weekByWeek.map((week, idx) => {
                const isCurrent = currentWeek === week.week;
                const linkedPhase =
                  week.phaseIndex !== undefined &&
                  phases[week.phaseIndex];
                return (
                  <div
                    key={idx}
                    className={`bg-slate-950/50 p-4 rounded-xl border ${
                      isCurrent
                        ? 'border-blue-500/50 ring-1 ring-blue-500/20'
                        : 'border-white/8'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <Badge className="bg-blue-500/20 text-[#3b82f6] border-0">
                        Week {week.week}
                      </Badge>
                      {isCurrent && (
                        <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs">
                          Current
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      Focus:{' '}
                      {week.focus || 'Implement key workflows'}
                    </p>
                    {linkedPhase && (
                      <p className="text-[11px] text-slate-500 mb-2">
                        Anchored in: {linkedPhase.name}
                      </p>
                    )}
                    <ul className="text-xs text-slate-300 space-y-1">
                      {week.tasks.map((task, tidx) => (
                        <li
                          key={tidx}
                          className="flex items-start gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Steps by Phase */}
      {phases.map((phase, pIdx) => {
        const phaseSteps = allSteps.filter(
          (s) => s.phaseId === pIdx
        );
        const hasWeeklyTasks = Array.isArray(phase.weeklyTasks) && phase.weeklyTasks.length > 0;
        if (phaseSteps.length === 0 && !hasWeeklyTasks) return null;
        const phaseCompleted = phaseSteps.every(
          (s) => isStepDone(s.id)
        );

        return (
          <Card
            key={phase.id || pIdx}
            className="bg-slate-900/70 backdrop-blur-xl border border-white/10"
          >
            <CardHeader
              className="cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() =>
                setExpandedPhase(
                  expandedPhase === pIdx ? null : pIdx
                )
              }
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      phaseCompleted
                        ? 'bg-[#3b82f6]'
                        : 'bg-blue-400'
                    }`}
                  />
                  <CardTitle className="text-white text-lg">
                    {phase.name}
                  </CardTitle>
                  {phaseCompleted && (
                    <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs">
                      Complete
                    </Badge>
                  )}
                </div>
                <div className="text-slate-400">
                  {expandedPhase === pIdx ? (
                    <ChevronUp />
                  ) : (
                    <ChevronDown />
                  )}
                </div>
              </div>

              <p className="text-sm text-slate-400 mt-1">
                {phase.focus}
              </p>

              {phase.expectedOutcome && (
                <p className="text-xs text-[#3b82f6] mt-1">
                  Expected impact: {phase.expectedOutcome}
                </p>
              )}

              {(phase.ownerHint || phase.riskFocus) && (
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                  {phase.ownerHint && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> Recommended
                      owner: {phase.ownerHint}
                    </span>
                  )}
                  {phase.riskFocus && (
                    <span className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 text-amber-400" />{' '}
                      Watch for: {phase.riskFocus}
                    </span>
                  )}
                </div>
              )}

              {phase.dataNeeded && phase.dataNeeded.length > 0 && (
                <p className="mt-2 text-xs text-slate-500">
                  Bring: {phase.dataNeeded.join(' • ')}
                </p>
              )}
            </CardHeader>
            <AnimatePresence>
              {expandedPhase === pIdx && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  <CardContent className="pt-0">
                    {/* ── Week-by-week breakdown (LLM-generated). Graceful: if absent, skip. ── */}
                    {Array.isArray(phase.weeklyTasks) && phase.weeklyTasks.length > 0 && (
                      <div className="mb-5 space-y-3 border-b border-white/8 pb-5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold uppercase tracking-wider text-blue-400">
                            Week-by-week plan
                          </p>
                          <span className="text-[10px] text-slate-500">
                            {phase.weeklyTasks.length} week{phase.weeklyTasks.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        {phase.weeklyTasks.map((wk, wIdx) => (
                          <div
                            key={`wk-${pIdx}-${wIdx}`}
                            className="rounded-lg border border-white/8 bg-slate-900/60 p-3"
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div>
                                <p className="text-[11px] text-slate-500 uppercase tracking-wider">
                                  Week {wk.weekNumber ?? wIdx + 1}
                                </p>
                                <p className="text-sm font-semibold text-white mt-0.5">
                                  {wk.title || `Week ${wk.weekNumber ?? wIdx + 1}`}
                                </p>
                              </div>
                              {wk.successMetric && (
                                <div className="text-right max-w-[55%]">
                                  <p className="text-[10px] text-[#3b82f6] uppercase tracking-wider">
                                    Success metric
                                  </p>
                                  <p className="text-[11px] text-slate-300 leading-snug mt-0.5">
                                    {wk.successMetric}
                                  </p>
                                </div>
                              )}
                            </div>

                            {Array.isArray(wk.tasks) && wk.tasks.length > 0 && (
                              <ul className="space-y-1.5 mt-2">
                                {wk.tasks.map((task, tIdx) => (
                                  <li
                                    key={`wk-${pIdx}-${wIdx}-t-${tIdx}`}
                                    className="flex items-start gap-2 text-xs"
                                  >
                                    <span className="text-[10px] font-semibold text-slate-500 min-w-[60px] mt-0.5">
                                      {task.days || `Day ${tIdx + 1}`}
                                    </span>
                                    <span className="flex-1 text-slate-300 leading-relaxed">
                                      {task.task || task}
                                    </span>
                                    {task.estimatedMinutes ? (
                                      <Badge
                                        className="bg-blue-500/10 text-[#3b82f6] border border-blue-500/25 text-[9px] shrink-0"
                                      >
                                        ~{task.estimatedMinutes} min
                                      </Badge>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {phaseSteps.map((step) => (
                      <PhaseStep
                        key={step.id}
                        step={step}
                        phaseId={pIdx}
                        stepIndex={step.id}
                        completed={isStepDone(step.id)}
                        onToggle={() => toggleStep(step.id)}
                        expanded={expandedSteps.has(step.id)}
                        onExpand={() =>
                          toggleStepExpanded(step.id)
                        }
                      />
                    ))}
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        );
      })}

      {/* Quarterly Reviews */}
      {quarterlyReviews.length > 0 && (
        <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-purple-400">
              <TrendingUp className="h-5 w-5" /> Quarterly Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {quarterlyReviews.map((review, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3"
                >
                  <div className="w-5 h-5 bg-purple-500/20 rounded-lg flex items-center justify-center mt-0.5">
                    <Check className="w-3 h-3 text-purple-400" />
                  </div>
                  <span className="text-sm text-slate-300">
                    {review}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Suggested KPIs */}
      {metrics.length > 0 && (
        <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#3b82f6]">
              <Target className="h-5 w-5" /> Suggested KPIs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {metrics.map((metric, idx) => (
                <Badge
                  key={idx}
                  className="bg-white/8 text-slate-300 text-sm px-3 py-1 border border-white/8"
                >
                  {metric}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export/Share buttons */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          size="sm"
          className="border-white/15 text-slate-300"
          onClick={exportStepsAsCSV}
        >
          <Download className="mr-2 h-4 w-4" /> Export as CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="border-white/15 text-slate-300"
          onClick={() => window.print()}
        >
          <Printer className="mr-2 h-4 w-4" /> Print Checklist
        </Button>
      </div>
    </div>
  );
};
